import React, { useState, useCallback, useEffect } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import SendIcon from "@styles/all-images/icons/send.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import DeleteIcon from "@styles/all-images/icons/deleteblue.svg";
import RevertIcon from "@styles/all-images/icons/revert.svg";
import ReUploadIcon from "@styles/all-images/icons/uploadblue.svg";
import HistoryIcon from "@styles/all-images/icons/timeblue.svg";
import dateHandler from "@/composables/dateUtil";
import FilterIcon from "@styles/all-images/icons/rejectIcon.svg";

import { toast } from "react-toastify";
import FileUploadModal from "@/components/common/FileUploadModal";
import StatusChips, {
  StatusChipEnumType,
} from "@/components/common/StatusChips";
import { useNavigate } from "react-router-dom";
import CreateTermsSheet from "../TermsSheet/CreateTermsSheet";
import { DocumentType } from "../../DocumentView/components/DocumentDetails";
import {
  IMTSDocumentApprovalStatus,
  ImTsDocumentStatus,
} from "@/constants/enums/im-ts-document-status.enum";
import FilterModal from "@/components/common/FilterModal";
import { AcceptedFileTypes } from "@/components/common/inputs/FileUpload";
import { usePermissionState } from "@/providers/PermissionProvider";
import { DocumentsPermissions } from "@/constants/permission-modules/base-permissions";
interface CreatedTermSheetTableProps {
  tabId: number;
}

const CreatedTermSheetTable: React.FC<CreatedTermSheetTableProps> = ({
  tabId,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDateWithTime } = dateHandler();
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [isUploadedModalOpen, setIsUploadedModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(0);
  const { isPermitted } = usePermissionState();

  const handleView = useCallback((doc: any) => {
    navigate(`/view-im-ts-document/${doc.id}?type=ts&history=false`);
  }, []);

  const handleSend = useCallback(
    async (doc: any) => {
      try {
        const { authenticatedCall } = actionsHandler("sendTermSheet");
        const response = await dispatch(
          authenticatedCall({
            method: "put",
            customParams: [doc.id + "/"],
            payload: { doc_id: doc.id },
          }),
        );
        toast.success(response.data.message);
        setForceRefresh(true);
      } catch (error) {
        console.error("Error sending term sheet:", error);
      }
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    async (doc: any) => {
      setIsCreateModalOpen(true);
      setIsLoadingDocument(true);

      try {
        const { authenticatedCall } = actionsHandler("viewTSDocument");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [doc.id + "/"],
          }),
        );

        if (response?.ok) {
          setSelectedDocument(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching document details:", error);
      } finally {
        setIsLoadingDocument(false);
      }
    },
    [dispatch],
  );

  const handleDelete = useCallback((doc: any) => {
    setDocumentToDelete(doc);
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      const { authenticatedCall } = actionsHandler("deleteTermSheet");
      const response = await dispatch(
        authenticatedCall({
          method: "delete",
          customParams: [documentToDelete.id + "/"],
          payload: { id: documentToDelete.id },
        }),
      );
      toast.success(response.data.message);
      setForceRefresh(true);
    } catch (error) {
      console.error("Error deleting term sheet:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    }
  }, [dispatch, documentToDelete]);

  const handleRevert = useCallback(
    async (doc: any) => {
      try {
        const { authenticatedCall } = actionsHandler("revertTermSheet");
        const response = await dispatch(
          authenticatedCall({
            method: "put",
            customParams: [doc.id + "/"],
            payload: { doc_id: doc.id },
          }),
        );
        toast.success(response.data.message);
        setForceRefresh(true);
        return response;
      } catch (error) {
        console.error("Error reverting term sheet:", error);
      }
    },
    [dispatch],
  );

  const handleReUpload = useCallback((doc: any) => {
    setIsUploadedModalOpen(true);
    setSelectedDocument(doc);
  }, []);

  const handleHistory = useCallback(
    async (doc: any) => {
      navigate(`/document/im-document/history/${doc.id}/${DocumentType.TS}`);
    },
    [dispatch],
  );

  const columns = [
    {
      header: "Document Name",
      accessor: "document_name",
      sortable: true,
      sortKey: "document_name",
    },
    {
      header: "Super Trader(T4)",
      accessor: "super_trader",
      sortable: true,
      sortKey: "super_trader",
    },
    {
      header: "Created on Date & Time",
      accessor: (row: any) => formatDateWithTime(row.created_on),
      sortable: true,
      sortKey: "created_on",
    },
    {
      header: "Sent on Date & Time",
      accessor: (row: any) =>
        row.sent_on === "-" ? "-" : formatDateWithTime(row.sent_on),
      sortable: true,
      sortKey: "sent_on",
    },
    {
      header: "Document Status",
      accessor: (row: any) => (
        <StatusChips
          status={row.document_status}
          type={StatusChipEnumType.IMTSDocument}
          showRejectionNote={true}
          rejectionNote={row.note}
        />
      ),
      sortable: true,
      sortKey: "document_status",
    },
    {
      header: "Approval Status",
      accessor: (row: any) => (
        <StatusChips
          status={row.approval_status}
          type={StatusChipEnumType.IMTSDocumentApproval}
          showRejectionNote={true}
          rejectionNote={row.note}
        />
      ),
      sortable: true,
      sortKey: "approval_status",
    },
    {
      header: "Actions",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row)} title="View">
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>
          {row.document_status === ImTsDocumentStatus.NotSent && (
            <>
              {isPermitted(DocumentsPermissions.SEND_TERMS_SHEET_DOCUMENTS) && (
                <button onClick={() => handleSend(row)} title="Send">
                  <img
                    src={SendIcon}
                    alt="Send"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )}
              {isPermitted(
                DocumentsPermissions.UPDATE_TERMS_SHEET_DOCUMENTS,
              ) && (
                <button onClick={() => handleEdit(row)} title="Edit">
                  <img
                    src={EditIcon}
                    alt="Edit"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )}
              {isPermitted(
                DocumentsPermissions.DELETE_TERMS_SHEET_DOCUMENTS,
              ) && (
                <button onClick={() => handleDelete(row)} title="Delete">
                  <img
                    src={DeleteIcon}
                    alt="Delete"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )}
            </>
          )}
          {row.document_status === ImTsDocumentStatus.Sent &&
            row.approval_status === IMTSDocumentApprovalStatus.Pending &&
            isPermitted(DocumentsPermissions.UPDATE_TERMS_SHEET_DOCUMENTS) && (
              <button onClick={() => handleRevert(row)} title="Revoke">
                <img
                  src={RevertIcon}
                  alt="Revert"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
          {row.document_status === ImTsDocumentStatus.Sent &&
            row.approval_status === IMTSDocumentApprovalStatus.Rejected &&
            isPermitted(DocumentsPermissions.UPDATE_TERMS_SHEET_DOCUMENTS) && (
              <button onClick={() => handleReUpload(row)} title="Re-upload">
                <img
                  src={ReUploadIcon}
                  alt="Re-upload"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
          {row.approval_status === IMTSDocumentApprovalStatus.Rejected &&
            row.is_history_available && (
              <button onClick={() => handleHistory(row)} title="History">
                <img
                  src={HistoryIcon}
                  alt="History"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
        </div>
      ),
    },
  ];
  useEffect(() => {
    if (selectedCompanyId !== undefined) {
      setForceRefresh((prev) => !prev);
    }
  }, [selectedCompanyId]);

  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const { authenticatedCall } = actionsHandler("createdTermSheetList");

        const queryParams = new URLSearchParams({
          limit: params.limit || 10,
          page: params.page || 1,
          search: params.search || "",
          ...(params.sortColumn && {
            ordering:
              (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
          }),
          ...(selectedCompanyId && {
            company_id: selectedCompanyId,
          }),
        }).toString();

        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: queryParams,
          }),
        );

        return response;
      } catch (error) {
        console.error("Error fetching created term sheets:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1, limit: 10 },
          },
        };
      }
    },
    [dispatch, selectedCompanyId],
  );

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={fetchDocumentsWrapper}
        forceRefresh={forceRefresh}
        addButton={
          isPermitted(DocumentsPermissions.CREATE_TERMS_SHEET_DOCUMENTS)
            ? {
                text: "Add Terms Sheet",
                onClick: () => setIsCreateModalOpen(true),
              }
            : undefined
        }
        filterButton={{
          text: "Filter",
          icon: FilterIcon,
          onClick: () => setIsFilterModalOpen(true),
        }}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        noDataMessage="No term sheet documents found"
        noSearchDataMessage="No matching term sheet documents found"
      />

      {showDeleteConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">Confirm Delete</h2>
              <button onClick={() => setShowDeleteConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to delete this term sheet?
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                {isDeleting ? "Deleting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <CreateTermsSheet
          isModal={true}
          isModalOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedDocument(null);
          }}
          onSubmit={() => {
            setForceRefresh((prev) => !prev);
            setIsCreateModalOpen(false);
            setSelectedDocument(null);
          }}
          initialData={selectedDocument}
          mode={selectedDocument ? "edit" : "create"}
          isLoading={isLoadingDocument}
        />
      )}
      {isUploadedModalOpen && (
        <FileUploadModal
          open={isUploadedModalOpen}
          title="File Upload"
          onClose={() => {
            setIsUploadedModalOpen(false);
            setSelectedDocument(null);
          }}
          endpoint="reuploadTsDocument"
          maxFileSize={250 * 1000 * 1000}
          acceptedFileTypes={[AcceptedFileTypes.PDF]}
          folderId={tabId}
          isUpdateId={Number(selectedDocument?.id)}
          onSuccess={() => {
            setForceRefresh(true);
            setIsUploadedModalOpen(false);
          }}
        />
      )}
      {isFilterModalOpen && (
        <FilterModal
          onClose={() => setIsFilterModalOpen(false)}
          onApply={(companyId) => {
            setSelectedCompanyId(companyId || 0);
            setIsFilterModalOpen(false);
          }}
          isOpen={isFilterModalOpen}
          currentFilter={selectedCompanyId || 0}
        />
      )}
    </>
  );
};

export default CreatedTermSheetTable;
